// UIContext.tsx
// Contexte UI : état sidebar, filtres globaux actifs.

import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

interface UIContextValue {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  /** Année active pour les filtres (défaut : année courante) */
  activeYear: number;
  setActiveYear: (year: number) => void;
  /** Famille de risque sélectionnée pour les filtres (null = toutes) */
  activeFamilyId: string | null;
  setActiveFamilyId: (familyId: string | null) => void;
  /** Version DUERP sélectionnée par site (siteId → versionId) */
  duerpVersionBySite: Record<string, string>;
  setDuerpVersionForSite: (siteId: string, versionId: string) => void;
}

const UIContext = createContext<UIContextValue | null>(null);

export function UIProvider({ children }: { children: ReactNode }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeYear, setActiveYear] = useState<number>(new Date().getFullYear());
  const [activeFamilyId, setActiveFamilyId] = useState<string | null>(null);
  const [duerpVersionBySite, setDuerpVersionBySite] = useState<Record<string, string>>({});

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  const setDuerpVersionForSite = (siteId: string, versionId: string) =>
    setDuerpVersionBySite((prev) => ({ ...prev, [siteId]: versionId }));

  return (
    <UIContext.Provider
      value={{
        isSidebarOpen,
        toggleSidebar,
        activeYear,
        setActiveYear,
        activeFamilyId,
        setActiveFamilyId,
        duerpVersionBySite,
        setDuerpVersionForSite,
      }}
    >
      {children}
    </UIContext.Provider>
  );
}

export function useUI(): UIContextValue {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be used within UIProvider');
  return ctx;
}
